import React, { useEffect, useMemo, useState } from 'react';
import pluralize from 'pluralize';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';

import { type ILead, useUpdateLeads } from 'src/services/api';
import { FlexColumn } from 'src/components/flex';
import SelectProperty from 'src/components/input/select-property/SelectProperty';

type Props = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    leads: ILead[];
};

export default function BulkChangeLeadPropertyModal({
    open,
    onClose,
    onSuccess,
    leads,
}: Props) {
    const {
        mutateAsync: updateLeads,
        isLoading: isUpdatingLeads,
    } = useUpdateLeads();

    const initialPropertyId = useMemo(() => {
        if (!leads.length) {
            return undefined;
        }

        const propertyIds = leads.map((lead) => lead.property_id ?? null);
        const areAllLeadsSameProperty = new Set(propertyIds).size === 1;

        if (!areAllLeadsSameProperty) {
            return undefined;
        }

        const propertyId = propertyIds[0];
        return propertyId === null ? '' : String(propertyId);
    }, [leads]);

    const [selectedPropertyId, setSelectedPropertyId] = useState<string | undefined>(undefined);
    const [hasTouched, setHasTouched] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }

        setSelectedPropertyId(initialPropertyId);
        setHasTouched(false);
    }, [open, initialPropertyId]);

    const handleChange = (value?: string) => {
        setSelectedPropertyId(value);
        setHasTouched(true);
    };

    const save = async () => {
        if (!hasTouched || selectedPropertyId === undefined) {
            return;
        }

        const propertyId = selectedPropertyId === '' ? -1 : Number(selectedPropertyId);

        await updateLeads(leads.map((lead) => ({
            uuid: lead.uuid,
            property_id: propertyId,
        })));

        onSuccess();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: '100%' } }}
        >
            <DialogTitle>Change Property</DialogTitle>
            <DialogContent>
                <FlexColumn rowGap={2} pt={1}>
                    <SelectProperty
                        variant="standard"
                        label="Property"
                        value={selectedPropertyId}
                        onChange={handleChange}
                        isClearable
                        clearableText="No property"
                        data-testid="bulk-change-property-select"
                    />

                    <DialogContentText>
                        This action will update the property for {leads.length} {pluralize('lead', leads.length)}.
                    </DialogContentText>
                </FlexColumn>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    color="dark"
                    onClick={onClose}
                    disabled={isUpdatingLeads}
                    data-testid="bulk-change-property-cancel-button"
                >
                    Cancel
                </Button>

                <LoadingButton
                    variant="contained"
                    onClick={save}
                    disabled={!hasTouched}
                    loading={isUpdatingLeads}
                    data-testid="bulk-change-property-save-button"
                >
                    Save
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
